// Toptan göçme zemin modeli: dilim yönteminin sorduğu üç şey — yüzey kotu, sütun ağırlığı, taban dayanımı/boşluk suyu.
// İki kurucu: duvar kesiti (dolgu tabakaları + temel zemini + geçilmez duvar gövdesi + YASS + sürşarj) ve doğrulama için homojen şev.
import { deg2rad } from "../math/angles";
import type { Pt } from "../math/polygon";
import type { Section } from "../geometry/section";
import type { WallInput } from "../types/input";

export type SlopeMaterial = "backfill" | "foundation" | "wall";

/** Sütun çıktısı (yeniden kullanılan nesne; allocation yok) */
export interface ColumnOut { w: number; yc: number }
/** Taban çıktısı */
export interface BaseOut { tanPhi: number; c: number; u: number; material: SlopeMaterial }

export interface GroundModel {
  /** Yüzey kırık çizgisi; x artan, uçlar çok uzağa uzatılmış (kesişim için) */
  surface: Pt[];
  surfaceY(x: number): number;
  /** x sütununda yb..ys arası birim genişlik ağırlığı w (kN/m²) ve ağırlık merkezi kotu; sürşarj dahil */
  column(x: number, yb: number, ys: number, out: ColumnOut): void;
  /** (x, yb) taban noktasında kayma dayanımı ve boşluk suyu basıncı */
  base(x: number, yb: number, out: BaseOut): void;
  /** Çıkış (ön) ve giriş (arka) noktalarının izinli olduğu yüzey bölgeleri */
  allowExit(x: number): boolean;
  allowEntry(x: number): boolean;
  /** Bunun altına inen daireler geçersiz (sert taban) */
  firmBaseY?: number;
}

const FAR = 1e4;
const EPS = 1e-9;
/** Duvar gövdesi: geçilmez malzeme (çok yüksek kohezyon, φ = 0) */
export const WALL_C = 1e4;

/** Kırık çizgide doğrusal yüzey kotu (x dışarıdaysa uç değer) */
export function polylineY(pts: Pt[], x: number): number {
  const n = pts.length;
  if (x <= pts[0]!.x) return pts[0]!.y;
  for (let i = 1; i < n; i++) {
    const q = pts[i]!;
    if (x <= q.x) {
      const p = pts[i - 1]!;
      const dx = q.x - p.x;
      return dx < EPS ? Math.max(p.y, q.y) : p.y + ((x - p.x) * (q.y - p.y)) / dx;
    }
  }
  return pts[n - 1]!.y;
}

/** Çokgen kenarları düz dizide (x0,y0,x1,y1 …) — sıcak döngüde nesne erişimi yok */
function edgeArray(poly: Pt[]): Float64Array {
  const n = poly.length, e = new Float64Array(4 * n);
  for (let i = 0; i < n; i++) { const p = poly[i]!, q = poly[(i + 1) % n]!; e[4 * i] = p.x; e[4 * i + 1] = p.y; e[4 * i + 2] = q.x; e[4 * i + 3] = q.y; }
  return e;
}

/** Çokgenin x düşey doğrusuyla kesişim kotları (yarı açık kural), artan sıralı; `out`a yazar, adet döner. */
function verticalCrossings(edges: Float64Array, x: number, out: Float64Array): number {
  let k = 0;
  for (let i = 0; i < edges.length; i += 4) {
    const px = edges[i]!, qx = edges[i + 2]!;
    if ((px <= x && qx > x) || (qx <= x && px > x)) {
      const py = edges[i + 1]!, qy = edges[i + 3]!;
      const y = py + ((x - px) * (qy - py)) / (qx - px);
      // ekleme sıralaması (k küçük)
      let j = k;
      while (j > 0 && out[j - 1]! > y) { out[j] = out[j - 1]!; j--; }
      out[j] = y;
      k++;
    }
  }
  return k;
}

/**
 * Duvar kesiti zemin modeli.
 *  - Ön zemin: x ≤ ön yüz/yüzey kesişimi, kot y_F = taban + Df; malzeme temel zemini.
 *  - Duvar: çokgen, geçilmez (WALL_C); ağırlığı γ_duvar.
 *  - Arka: tepe arka köşeden β eğimli yüzey; taban üstü dolgu tabakaları (kalınlıklar tepeden), taban altı temel zemini.
 *  - YASS: arkada `water.backLevel`, önde `water.frontLevel`, taban altında doğrusal geçiş (kaldırma konvansiyonuyla aynı); u = γw·(h_p − y_b).
 *  - Sürşarj q: tepe arka köşesinden itibaren dolgu yüzeyinde.
 */
export function wallGroundModel(input: WallInput, section: Section): GroundModel {
  const poly = section.polygon;
  const baseY = section.baseY, yTop = section.yTop;
  const yF = section.toe.y + input.geometry.embedmentDepth;   // ön zemin: burundan Df (eğik tabanda burun topuktan yüksek)
  if (yF >= yTop - 1e-6) throw new Error("Toptan göçme için ön zemin kotu duvar tepesinin altında olmalı (Df < H)");
  const topBack = section.topBack;
  const topL = Math.min(...poly.filter((p) => Math.abs(p.y - yTop) < 1e-6).map((p) => p.x));
  const tanB = Math.tan(deg2rad(input.backfill.slopeAngle));

  // Ön/taç zinciri: topBack'ten CCW ilerleyip buruna kadar (sağdan sola) → ters çevir (soldan sağa)
  const n = poly.length;
  const iTop = poly.findIndex((p) => Math.abs(p.x - topBack.x) < 1e-9 && Math.abs(p.y - topBack.y) < 1e-9);
  const chain: Pt[] = [];
  for (let k = 0; k <= n; k++) { const v = poly[(iTop + k) % n]!; chain.push(v); if (k > 0 && v === section.toe) break; }
  chain.reverse();   // burun → … → topBack
  // y_F üstünde kalan kısmı al; ilk yukarı kesişim ön zeminle birleşme noktası
  const surface: Pt[] = [{ x: -FAR, y: yF }];
  let xCross = section.toe.x;
  let started = false;
  for (let i = 0; i < chain.length; i++) {
    const v = chain[i]!;
    if (!started) {
      if (v.y > yF + 1e-9) {
        const p = chain[i - 1] ?? { x: v.x, y: yF };
        xCross = p.y < yF - 1e-9 ? p.x + ((yF - p.y) * (v.x - p.x)) / (v.y - p.y) : p.x;
        surface.push({ x: xCross, y: yF }, v);
        started = true;
      }
      continue;
    }
    surface.push({ x: v.x, y: Math.max(v.y, yF) });
  }
  surface.push({ x: topBack.x + FAR, y: yTop + FAR * tanB });
  // x monoton olmayan (sarkık) köşeleri at
  const surf: Pt[] = [];
  for (const p of surface) { if (surf.length === 0 || p.x >= surf[surf.length - 1]!.x - 1e-9) surf.push(p); }

  // Malzemeler
  const layers = input.backfill.layers.map((L) => ({ gamma: L.gamma, gammaSat: L.gammaSat ?? L.gamma, tanPhi: Math.tan(deg2rad(L.phi)), c: L.c }));
  const nL = layers.length;
  const layerBottom: number[] = [];   // her tabakanın alt kotu (son tabaka −∞'a uzar)
  { let y = yTop; for (let j = 0; j < nL; j++) { y -= input.backfill.layers[j]!.thickness; layerBottom.push(j === nL - 1 ? -Infinity : y); } }
  const fs = input.foundationSoil;
  const found = { gamma: fs.gamma, tanPhi: Math.tan(deg2rad(fs.phi)), c: fs.c };
  const gammaWall = input.material.gamma;
  const q = input.loads.surcharge;
  const gw = input.water.gammaWater;
  const hwBack = input.water.backLevel, hwFront = input.water.frontLevel;
  const toeX = section.toe.x, heelX = section.heel.x;
  // Temel zemini üst sınırı: eğik tabanda burun–topuk arası taban doğrusu, dışında topuk (en alt) kotu
  const sB = section.baseSlope;
  const yBaseAt = (x: number): number => (sB > 0 && x > toeX && x < heelX ? section.toe.y - sB * (x - toeX) : baseY);

  const layerAt = (y: number): number => { for (let j = 0; j < nL; j++) if (y >= layerBottom[j]!) return j; return nL - 1; };
  const piezo = (x: number): number => {
    if (x >= heelX) return hwBack ?? -Infinity;
    if (x <= toeX) return hwFront ?? -Infinity;
    if (hwBack === null && hwFront === null) return -Infinity;
    const hb = hwBack ?? baseY, hf = hwFront ?? baseY;
    return hf + ((x - toeX) * (hb - hf)) / (heelX - toeX);
  };

  // Düşey kesişimler: aynı x için column() ve base() art arda çağrılır → son sonuç önbellekte
  const edges = edgeArray(poly);
  const cross = new Float64Array(poly.length);
  let crossX = NaN, crossK = 0;
  const crossingsAt = (x: number): number => {
    if (x !== crossX) { crossK = verticalCrossings(edges, x, cross); crossX = x; }
    return crossK;
  };
  // [ya, yb] zemin aralığının ağırlığı ve momenti (x sütunu); parçalı: taban kotu, tabaka sınırları, YASS
  const soilAcc = { w: 0, m: 0 };
  const addSoil = (x: number, ya: number, yb: number): void => {
    if (yb - ya < EPS) return;
    // taban altı: temel zemini
    const yB = yBaseAt(x);
    if (ya < yB) { const yt = Math.min(yb, yB); soilAcc.w += found.gamma * (yt - ya); soilAcc.m += found.gamma * (yt - ya) * (ya + yt) / 2; ya = yt; if (yb - ya < EPS) return; }
    if (x < topL) { soilAcc.w += found.gamma * (yb - ya); soilAcc.m += found.gamma * (yb - ya) * (ya + yb) / 2; return; }   // ön zemin
    // dolgu: tabaka ve su bantları
    let y = ya;
    while (yb - y > EPS) {
      const j = layerAt(y + 1e-9);
      let yt = yb;
      // bir sonraki sınır: tabakanın üstü (üstteki tabakanın altı) ya da YASS
      if (j > 0 && layerBottom[j - 1]! > y + 1e-9 && layerBottom[j - 1]! < yt) yt = layerBottom[j - 1]!;
      if (hwBack !== null && hwBack > y + 1e-9 && hwBack < yt) yt = hwBack;
      const L = layers[j]!;
      const g = hwBack !== null && (y + yt) / 2 < hwBack ? L.gammaSat : L.gamma;
      soilAcc.w += g * (yt - y); soilAcc.m += g * (yt - y) * (y + yt) / 2;
      y = yt;
    }
  };

  return {
    surface: surf,
    surfaceY: (x) => polylineY(surf, x),
    column(x, yb, ys, out) {
      soilAcc.w = 0; soilAcc.m = 0;
      const k = crossingsAt(x);
      let y = yb;
      for (let i = 0; i + 1 < k; i += 2) {
        const w0 = Math.max(cross[i]!, yb), w1 = Math.min(cross[i + 1]!, ys);
        if (w1 - w0 < EPS) continue;
        addSoil(x, y, w0);
        soilAcc.w += gammaWall * (w1 - w0); soilAcc.m += gammaWall * (w1 - w0) * (w0 + w1) / 2;
        y = w1;
      }
      addSoil(x, y, ys);
      if (q > 0 && x >= topBack.x - 1e-9) { soilAcc.w += q; soilAcc.m += q * ys; }
      out.w = soilAcc.w;
      out.yc = soilAcc.w > EPS ? soilAcc.m / soilAcc.w : (yb + ys) / 2;
    },
    base(x, yb, out) {
      const hp = piezo(x);
      out.u = hp > yb ? gw * (hp - yb) : 0;
      if (yb < yBaseAt(x) - 1e-9) { out.tanPhi = found.tanPhi; out.c = found.c; out.material = "foundation"; return; }
      const k = crossingsAt(x);
      for (let i = 0; i + 1 < k; i += 2) if (yb > cross[i]! + 1e-9 && yb < cross[i + 1]! - 1e-9) { out.tanPhi = 0; out.c = WALL_C; out.u = 0; out.material = "wall"; return; }
      if (x < topL) { out.tanPhi = found.tanPhi; out.c = found.c; out.material = "foundation"; return; }
      const L = layers[layerAt(yb)]!;
      out.tanPhi = L.tanPhi; out.c = L.c; out.material = "backfill";
    },
    allowExit: (x) => x <= xCross + 1e-6,
    allowEntry: (x) => x >= topBack.x - 1e-6,
  };
}

export interface HomogeneousSlope {
  toe: Pt; crest: Pt; gamma: number; phiDeg: number; c: number;
  /** YASS yüzeyde: u = γw·h (Slide el hesabı konvansiyonu) */
  waterAtSurface?: boolean; gammaWater?: number;
  firmBaseY?: number;
}

/** Doğrulama için homojen şev: yatay ön zemin (burun kotu) → düz şev → yatay taç. */
export function homogeneousSlopeModel(p: HomogeneousSlope): GroundModel {
  const surf: Pt[] = [{ x: -FAR, y: p.toe.y }, p.toe, p.crest, { x: FAR, y: p.crest.y }];
  const tanPhi = Math.tan(deg2rad(p.phiDeg));
  const gw = p.gammaWater ?? 9.81;
  const m: GroundModel = {
    surface: surf,
    surfaceY: (x) => polylineY(surf, x),
    column(_x, yb, ys, out) { out.w = p.gamma * (ys - yb); out.yc = (yb + ys) / 2; },
    base(x, yb, out) { out.tanPhi = tanPhi; out.c = p.c; out.material = "foundation"; out.u = p.waterAtSurface ? gw * Math.max(0, polylineY(surf, x) - yb) : 0; },
    allowExit: () => true,
    allowEntry: () => true,
  };
  if (p.firmBaseY !== undefined) m.firmBaseY = p.firmBaseY;
  return m;
}
